import { useState } from "react";

/**
 * Картинка из контента.
 *
 * Поддерживает локальные файлы из медиатеки (`/uploads/...`) и внешние URL.
 * Ленивая загрузка, плавное появление, аккуратная заглушка при ошибке —
 * чтобы битая ссылка не ломала вёрстку.
 */
export interface MediaImageContent {
  /** Путь или ссылка на изображение */
  src: string;
  /** Альтернативный текст — важен для SEO и доступности */
  alt?: string;
  /** Подпись под картинкой */
  caption?: string;
  /** Пропорции: 16/9, 4/3, 1/1, 21/9 */
  aspect?: string;
  /** Насколько скруглить углы */
  rounded?: boolean;
}

const ASPECT: Record<string, string> = {
  "16/9": "aspect-[16/9]",
  "4/3": "aspect-[4/3]",
  "3/2": "aspect-[3/2]",
  "1/1": "aspect-square",
  "21/9": "aspect-[21/9]",
  "9/16": "aspect-[9/16]",
};

export default function MediaImage({
  image,
  className = "",
  imgClassName = "",
  priority = false,
}: {
  image: MediaImageContent;
  className?: string;
  imgClassName?: string;
  priority?: boolean;
}) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  if (!image?.src) return null;

  const aspectClass = ASPECT[image.aspect ?? "16/9"] ?? ASPECT["16/9"];
  const roundedClass = image.rounded === false ? "" : "rounded-2xl";

  return (
    <figure className={`${className}`}>
      <div
        className={`relative w-full overflow-hidden ${aspectClass} ${roundedClass} bg-stone-100 dark:bg-white/[0.05]`}
      >
        {state === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
            <i className="ri-image-off-line text-2xl text-stone-400 dark:text-white/40"></i>
            <span className="text-xs text-stone-500 dark:text-white/50">
              Изображение недоступно
            </span>
          </div>
        ) : (
          <img
            src={image.src}
            alt={image.alt ?? ""}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onLoad={() => setState("ready")}
            onError={() => setState("error")}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              state === "ready" ? "opacity-100" : "opacity-0"
            } ${imgClassName}`}
          />
        )}
      </div>
      {image.caption && (
        <figcaption className="mt-2.5 text-xs text-stone-500 dark:text-white/50">
          {image.caption}
        </figcaption>
      )}
    </figure>
  );
}
